import { AppProvider } from '../../../common/appProvider';
import { AppSettings } from '../../application';
import { getSchemaFilePath, determineV4PageTemplateType, compareTemplateNames } from '../../../common';
import { createListReportConfig } from '../pages/listReport';
import { createObjectPageConfigV4 } from '../pages/objectPage';
import { createCustomPageConfig } from '../pages/fpmCustomPage';
import type {
    ExtensionFiles,
    Files,
    Manifest,
    SapUi5RoutingRoute,
    SapUi5RoutingRouteTarget,
    ImportAllConfigsParameters
} from '@sap/ux-specification-types';
import {
    v4,
    MANIFESTPATH,
    TemplateType,
    PageTypeV4,
    SchemaType,
    ManifestSection,
    ViewTypes,
    LogSeverity
} from '@sap/ux-specification-types';
import { importSettingsOfObject } from '../utils';
import type {
    ExtensionLogger,
    ImportObjectPageV4Parameters,
    ImportListReportV4Parameters,
    ImportFPMCustomPageV4Parameters,
    ImportAnalyticalListPageV4Parameters
} from '../../../..';
import { log } from '../../../../extensionLogger';
import i18next from 'i18next';
import { initI18n } from '../../../../i18n/i18n';
import type { Definition } from 'typescript-json-schema';
import { getEntityTypeEntitySetFromContextPath } from '../../../common/importProject';

export enum Constants {
    OptionalQuery = ':?query:',
    Component = 'sap/fe/AppComponent',
    Namespace = 'sap.fe.templates'
}

// Store controller names which should be handled lately
export const unhandledControllers: Array<string> = [];
// Store route associations
let associations: object = {};

/**
 * Sort in following way.
 * 1. string targets first.
 * 2. Then sort targets as array by elements count - from lowest to highest(ASC).
 *
 * @param {SapUi5RoutingRoute} route1 - First instance of router to compare.
 * @param {SapUi5RoutingRoute} route2 - Second instance of router to compare.
 * @returns {number} Compare result.
 */
function routesSorter(route1: SapUi5RoutingRoute, route2: SapUi5RoutingRoute): number {
    const target1 = route1.target;
    const target2 = route2.target;
    const isT1Array = Array.isArray(target1);
    const isT2Array = Array.isArray(target2);
    if (isT1Array && isT2Array) {
        return target1.length - target2.length;
    }
    if (!isT1Array && !isT2Array) {
        return 0;
    }
    return !isT1Array ? -1 : 1;
}

/**
 * Method to resolve target and return target name as string.
 * Thing is that we can receive target as array of targets and we need detect which target is unique target in that array.
 *
 * @param {SapUi5RoutingRouteTarget} target - Target to resolve.
 * @param {Array<string>} usedTargets - Array of resolved targets(which means that those targets are not unique anymore).
 * @returns {string} Resolved unique target namet.
 */
function resolveTarget(target: SapUi5RoutingRouteTarget, usedTargets: Array<string> = []): string {
    if (!Array.isArray(target)) {
        return target;
    }
    let targetParts: Array<string> = target;
    for (const usedTarget of usedTargets) {
        targetParts = targetParts.filter((targetPart) => !usedTarget.includes(targetPart));
    }
    return targetParts[0];
}

/**
 * Method which populates object with routings routes associations.
 *
 * @param {SapUi5RoutingRoute} routes - Routing routes.
 * @returns {object} Map object of association routes.
 */
function populateAssociations(routes: SapUi5RoutingRoute[]): object {
    const routesAssociations = {};
    routes = routes.concat().sort(routesSorter);
    const resolvedTargets = [];
    for (const route of routes) {
        const parts = route.pattern.split(':')[0].split('/');
        let target;
        if (Array.isArray(route.target)) {
            target = resolveTarget(route.target, resolvedTargets);
            resolvedTargets.push(route.target);
        } else {
            target = route.target;
        }
        for (const part of parts) {
            const prop = part.split('(')[0];
            routesAssociations[target] = routesAssociations[target] ? `${routesAssociations[target]}.${prop}` : prop;
        }
    }
    return routesAssociations;
}

/**
 * Determines the page type of a routing target.
 *
 * @param target - routing target, as defined in manifest
 * @param logger - logger for error messages
 * @param routingTargetId - ID of the routing target
 * @returns the page type
 */
function determinePageType(
    target: v4.RoutingTargetOptions,
    logger: ExtensionLogger,
    routingTargetId: string
): PageTypeV4 {
    let pageType: PageTypeV4;
    if (target['name']) {
        if (target['name'] === v4.FE_TEMPLATE_V4_CUSTOM_PAGE) {
            pageType = PageTypeV4.FPMCustomPage;
        } else if (
            target['name'].endsWith(PageTypeV4.ListReport) ||
            target['name'].endsWith(PageTypeV4.AnalyticalListPage)
        ) {
            pageType = PageTypeV4.ListReport;
        } else if (target['name'].endsWith(PageTypeV4.ObjectPage)) {
            pageType = PageTypeV4.ObjectPage;
        } else {
            pageType = PageTypeV4.CustomPage;
        }
    } else if ('viewId' in target) {
        pageType = PageTypeV4.CustomPage;
    } else {
        log(logger, {
            severity: LogSeverity.Error,
            message: i18next.t('INVALIDTARGETDEFINITION', { target: JSON.stringify(target) }),
            location: {
                path: MANIFESTPATH,
                range: [`${ManifestSection.ui5}/routing/targets/${routingTargetId}`]
            }
        });
        if (target['viewType'] in ViewTypes && 'viewName' in target) {
            pageType = PageTypeV4.CustomPage;
        }
    }
    return pageType;
}

export type ConversionParameters = {
    target: v4.RoutingTargetOptions;
    pageType: PageTypeV4;
    manifest: Manifest;
    schemas: Files;
    logger: ExtensionLogger;
    page: v4.Page;
    id: string;
    templateType: TemplateType;
    extensionFiles: ExtensionFiles;
};

/**
 * Converts the settings of a page to its corresponding configuration.
 *
 * @param params - The parameters required for the conversion, including target, pageType, manifest, schemas, logger, page, id, templateType, and extensionFiles.
 */
function convertSettingsToConfig(params: ConversionParameters) {
    const viewId = params.target['viewId'] || params.target['id'];
    const schemaId = getSchemaFilePath(params.page.pageType, params.id, viewId).filename;
    if (!schemaId || params.page.pageType === PageTypeV4.CustomPage) {
        return;
    }
    // Get the JSON schema for the page. If no page specific schema exists, fall back top the generic schema for the page type
    const jsonSchema: string =
        Object.keys(params.schemas).find((element) => element.includes(schemaId)) ||
        Object.keys(params.schemas).find((element) => element.includes(schemaId.slice(0, -1))) ||
        Object.keys(params.schemas).find((element) => element.includes(params.page.pageType));

    if (jsonSchema) {
        if (params.page.pageType === PageTypeV4.ObjectPage) {
            const importParameters: ImportObjectPageV4Parameters = {
                manifest: params.manifest,
                jsonSchema: params.schemas[jsonSchema],
                routingId: params.id,
                fragments: params.extensionFiles.fragments,
                logger: params.logger
            };
            params.page.config = createObjectPageConfigV4(importParameters);
        } else if (params.page.pageType === PageTypeV4.FPMCustomPage) {
            const importParameters: ImportFPMCustomPageV4Parameters = {
                manifest: params.manifest,
                jsonSchema: params.schemas[jsonSchema],
                logger: params.logger,
                routingId: params.id,
                fragments: params.extensionFiles.fragments,
                views: params.extensionFiles.views
            };
            params.page.config = createCustomPageConfig(importParameters);
        } else if (params.templateType === TemplateType.ListReportObjectPageV4) {
            const importParameters: ImportListReportV4Parameters = {
                manifest: params.manifest,
                jsonSchema: params.schemas[jsonSchema],
                routingId: params.id,
                logger: params.logger
            };
            params.page.config = createListReportConfig(importParameters, SchemaType.ListReport);
        } else if (params.templateType === TemplateType.AnalyticalListPageV4) {
            const importParameters: ImportAnalyticalListPageV4Parameters = {
                manifest: params.manifest,
                jsonSchema: params.schemas[jsonSchema],
                routingId: params.id,
                logger: params.logger
            };
            params.page.config = createListReportConfig(importParameters, SchemaType.AnalyticalListPage);
        } else if (
            params.templateType === TemplateType.FreestylePageV4 &&
            params.page.pageType === PageTypeV4.ListReport
        ) {
            const importParameters: ImportListReportV4Parameters = {
                manifest: params.manifest,
                jsonSchema: params.schemas[jsonSchema],
                routingId: params.id,
                logger: params.logger
            };
            const templateType = determineV4PageTemplateType(params.target as v4.SapUiAppPageV4);
            const schemaType =
                templateType === TemplateType.AnalyticalListPageV4
                    ? SchemaType.AnalyticalListPage
                    : SchemaType.ListReport;
            params.page.config = createListReportConfig(importParameters, schemaType);
        }
    }
}

/**
 * Updates the class variable collecting the unhandled controllers.
 *
 * @param target - current routing target
 */
function updateUnhandledControllers(target: v4.RoutingTargetOptions): void {
    if (target.options) {
        if ('viewName' in target.options.settings) {
            const viewParts = target.options.settings.viewName.split('.');
            unhandledControllers.push(viewParts[viewParts.length - 1]);
        }
    } else if ('viewId' in target) {
        // There no 'options' for custom page target -
        //    we need check target's controller source for navigation
        const viewParts = target.viewName.split('.');
        unhandledControllers.push(viewParts[viewParts.length - 1]);
    }
}

/**
 * Evaluates the target options in manifest, determines variantManagement and page navigation.
 *
 * @param target - target definition in manifest
 * @param page - page config, to be updated
 */
function setVariantManagmentAndNavigation(target: v4.RoutingTargetOptions, page: v4.PageV4): void {
    if (target.options) {
        if (target.options.settings.variantManagement) {
            page.variantManagement = target.options.settings.variantManagement;
        }
        page.navigation = {};
        for (const name in target.options.settings.navigation) {
            const navigationRoute = target.options.settings.navigation[name];
            page.navigation[name] = navigationRoute.detail;
        }
    }
}

/**
 * Evaluates the target options in manifest, determines entitySet, entityType and contextPath.
 *
 * @param target - target definition in manifest
 * @param page - page config, to be updated
 * @param importParameters - The parameters required for importing configurations, including manifest, schemas, and other related data.
 */
function setEntitySetEntityTypeContextPath(
    target: v4.RoutingTargetOptions,
    page: v4.PageV4,
    importParameters: ImportAllConfigsParameters
): void {
    if (target.options) {
        page.entitySet = target.options.settings?.entitySet;
        page.contextPath = target.options.settings?.contextPath;
        const { entitySet, entityType } = getEntityTypeEntitySetFromContextPath(
            importParameters.serviceAVT,
            page.contextPath,
            page.entitySet,
            importParameters.logger
        );
        page.entitySet = page.entitySet || entitySet?.name;
        page.entityType = entityType?.fullyQualifiedName;
    }
}

/**
 * Checks if the manifest's routing target represents a custom page definition.
 *
 * @param target - target definition in manifest
 * @param page - page config, to be updated
 * @param pageType - page type
 */
function evaluateCustomPageSettings(target: v4.RoutingTargetOptions, page: v4.Page, pageType: PageTypeV4): void {
    if ('viewId' in target) {
        // Populate view of custom page
        page.view = {
            id: target.viewId,
            name: target.viewName,
            ...(target.viewType && { viewType: target.viewType }),
            ...(target.viewPath && { path: target.viewPath }),
            ...(target.path && { path: target.path })
        };
    } else if ([PageTypeV4.CustomPage, PageTypeV4.FPMCustomPage].includes(pageType)) {
        let viewName =
            (target as v4.SapUi5RoutingTargetCustomPageV4New).options?.settings?.viewName || target['viewName'];
        let template = target.name;
        if (!viewName && pageType === PageTypeV4.CustomPage) {
            // Freestyle page - read 'viewName' from property 'name' if 'viewName' does not exist.
            viewName = template;
            template = undefined;
        }
        page.view = {
            ...(target.id && { id: target.id }),
            ...(viewName && { name: viewName }),
            ...(template && { template }),
            ...(target['viewType'] && { viewType: target['viewType'] })
        };
    }
}

/**
 * Determines a single page  information for app.json and creates the configuration for the page.
 *
 * @param {v4.SapUi5RoutingV4} routing - complete routing information from manifest
 * @param {string} id - routing target id in manifest
 * @param {v4.Pages} pages - list of all pages, the current page information gets added
 * @param {ImportAllConfigsParameters} importParameters - structure comprising all input parameters
 */
function getSinglePageAndConfig(
    routing: v4.SapUi5RoutingV4,
    id: string,
    pages: v4.Pages,
    importParameters: ImportAllConfigsParameters
): void {
    const target: v4.RoutingTargetOptions = routing.targets[id];
    const pageType = determinePageType(target, importParameters.logger, id);
    const page: v4.PageV4 = { pageType };

    // Entity set
    setEntitySetEntityTypeContextPath(target, page, importParameters);
    if (target.controlAggregation) {
        page.controlAggregation = target.controlAggregation;
    }
    if (associations[id] && associations[id].indexOf('.') !== -1) {
        const parts = associations[id].split('.');
        page.navigationProperty = parts[parts.length - 1];
    }
    // Custom page's view object
    evaluateCustomPageSettings(target, page, pageType);
    setVariantManagmentAndNavigation(target, page);
    updateUnhandledControllers(target);
    // Get layout from route
    const route = routing.routes.find((routingRoute) => routingRoute?.target && routingRoute.target.indexOf(id) > -1);
    if (route) {
        page.routePattern = route.pattern;
        if ('layout' in route) {
            page.defaultLayoutType = route.layout;
        }
    }
    // Store original page name/template - it is necessary for export as there can be custom templates like "sap.fe.ariba.ListReport"
    if (!page.view?.template && 'name' in target && target.name) {
        page.template = target.name;
    }

    pages[id] = page;
    // convert settings to config
    const conversionParameters: ConversionParameters = {
        target,
        pageType,
        manifest: importParameters.manifest,
        schemas: importParameters.appSchemas,
        logger: importParameters.logger,
        page,
        id,
        templateType: importParameters.templateType,
        extensionFiles: { views: importParameters.views, fragments: importParameters.fragments }
    };
    convertSettingsToConfig(conversionParameters);
}

/**
 * Determines the Pages(app.json) using routing configuration from manifest and creates the configuration for each page.
 *
 * @param {ImportAllConfigsParameters} importParameters - structure comprising all input parameters
 * @returns a list of configs for all pages
 */
export function getPagesAndConfigs(importParameters: ImportAllConfigsParameters): v4.Pages {
    const pages: v4.Pages = {};
    const routing: v4.SapUi5RoutingV4 = importParameters.manifest[ManifestSection.ui5].routing as v4.SapUi5RoutingV4;

    if (!routing || !routing.routes) {
        const message = i18next.t('NOROUTING');
        log(importParameters.logger, {
            severity: LogSeverity.Error,
            message,
            location: {
                path: MANIFESTPATH,
                range: [ManifestSection.ui5]
            }
        });
        return pages;
    }
    associations = populateAssociations(routing.routes || []);
    for (const id in routing.targets) {
        getSinglePageAndConfig(routing, id, pages, importParameters);
    }
    return pages;
}

/**
 * Get the id of the page that is to open when the application is started.
 *
 * @param routing UI5 routing configuration
 * @returns the id of the home page
 */
export function getHome(routing: v4.SapUi5RoutingV4): string | undefined {
    let routeHome;
    if (routing && routing.routes && routing.routes.length > 0) {
        for (const route of routing.routes) {
            //find the home, i.e. route which starts with ':?query:' pattern for List Report,
            //resp. doesn't contain '/' for Form Page
            if (route.pattern.startsWith(':?query:')) {
                routeHome = route;
                break;
            } else if (route.pattern.indexOf('/') === -1) {
                routeHome = route;
                continue;
            }
        }
        if (routeHome) {
            return resolveTarget(routeHome.target);
        } else {
            return resolveTarget(routing.routes[0].target);
        }
    } else {
        return undefined;
    }
}

/**
 * Method which detects app settings from manifest.
 * Currently only some settings are detected - Flexible Column Layout, viewPath, viewType, path.
 *
 * @param routing UI5 routing configuration.
 * @returns {AppSettings} Application settings.
 */
export function getSettings(routing: v4.SapUi5RoutingV4): AppSettings {
    const config = routing?.config;
    return config
        ? {
              ...(config.flexibleColumnLayout && { flexibleColumnLayout: config.flexibleColumnLayout }),
              ...(config.viewType && { viewType: config.viewType }),
              ...(config.viewPath && { path: config.viewPath }),
              ...(config.path && { path: config.path })
          }
        : {};
}

/**
 * Retrieves the pages from the manifest that match the specified target name.
 *
 * @param manifest - The manifest object containing the routing configuration.
 * @param targetName - The name of the target to filter pages by.
 * @returns An object containing the pages that match the target name.
 */
export function getPages(manifest: Manifest, targetName: string): v4.SapUiAppPagesV4 {
    const routing = manifest[ManifestSection.ui5].routing as v4.SapUi5RoutingV4;
    const pages: v4.SapUiAppPagesV4 = {};
    if (routing) {
        for (const id in routing.targets) {
            const v4Page = routing.targets[id];
            if (compareTemplateNames(targetName, v4Page['name'])) {
                pages[id] = v4Page as v4.SapUiAppPageV4;
            }
        }
    }
    return pages;
}

export class V4AppProvider extends AppProvider {
    /**
     *
     * @param importParameters - The parameters required for importing configurations, including manifest, schemas, and other related data.
     */
    public constructor(importParameters: ImportAllConfigsParameters) {
        initI18n();
        // Import settings
        const appSettings = new AppSettings();
        const schemaFile = getSchemaFilePath(SchemaType.Application);
        const appSchema = importParameters.appSchemas[schemaFile.filename] as Definition;
        importSettingsOfObject(appSettings, importParameters.manifest, appSchema.definitions.AppSettings, '', []);
        super(
            importParameters,
            getHome(importParameters.manifest[ManifestSection.ui5].routing as v4.SapUi5RoutingV4),
            getPagesAndConfigs(importParameters),
            {
                ...getSettings(importParameters.manifest[ManifestSection.ui5].routing as v4.SapUi5RoutingV4),
                ...appSettings
            }
        );
        this.unhandledControllers = unhandledControllers;
    }
}
